import { getTypeIcon, getTypeColor } from "./utils";
import {
  CloseRounded as CloseIcon,
  VisibilityRounded as ConditionIcon,
} from "@mui/icons-material";
import { Box, IconButton, Paper, Tooltip, Typography } from "@mui/material";
import { useState } from "react";

const PlacedVariableMarker = ({
  placement,
  onRemove,
  onMove,
  onEditCondition,
  zoom,
  containerRef,
}) => {
  const typeColor = getTypeColor(placement.variable.type);
  const [isDragging, setIsDragging] = useState(false);
  const hasCondition = Boolean(placement.condition);

  const handleMouseDown = (e) => {
    if (e.target.closest("button")) return;
    e.preventDefault();
    setIsDragging(true);

    const handleMouseMove = (moveEvent) => {
      if (!containerRef?.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const x = ((moveEvent.clientX - rect.left) / rect.width) * 100;
      const y = ((moveEvent.clientY - rect.top) / rect.height) * 100;
      onMove?.(placement.id, {
        x: Math.max(2, Math.min(98, x)),
        y: Math.max(1, Math.min(99, y)),
      });
    };

    const handleMouseUp = () => {
      setIsDragging(false);
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
  };

  const handleClick = (e) => {
    if (isDragging) return;
    if (e.target.closest("button")) return;
    onEditCondition?.(placement);
  };

  return (
    <Paper
      elevation={isDragging ? 6 : 3}
      onMouseDown={handleMouseDown}
      onClick={handleClick}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 0.5,
        px: 1,
        py: 0.5,
        borderRadius: 1,
        border: `2px solid ${typeColor}`,
        backgroundColor: hasCondition ? `${typeColor}10` : "white",
        cursor: isDragging ? "grabbing" : "grab",
        whiteSpace: "nowrap",
        fontSize: `${0.75 * (1 / zoom)}rem`,
        userSelect: "none",
        transition: isDragging ? "none" : "box-shadow 0.2s ease",
        "&:hover": { boxShadow: 4 },
      }}
    >
      {getTypeIcon(placement.variable.type)}
      <Typography
        variant={"caption"}
        sx={{ fontWeight: 500, color: typeColor, fontSize: "inherit" }}
      >
        {placement.variable.label}
      </Typography>
      {hasCondition && (
        <Tooltip
          title={
            <Box>
              <Typography variant={"caption"} sx={{ fontWeight: 600 }}>
                {"Show if:"}
              </Typography>
              <Typography variant={"caption"} display={"block"}>
                {placement.condition}
              </Typography>
            </Box>
          }
          arrow
        >
          <ConditionIcon
            sx={{
              fontSize: 12,
              color: "info.main",
              ml: 0.25,
            }}
          />
        </Tooltip>
      )}
      <IconButton
        size={"small"}
        onClick={(e) => {
          e.stopPropagation();
          onRemove(placement.id);
        }}
        sx={{
          p: 0.25,
          ml: 0.5,
          "&:hover": { color: "error.main" },
        }}
      >
        <CloseIcon sx={{ fontSize: 14 }} />
      </IconButton>
    </Paper>
  );
};

export default PlacedVariableMarker;
